/**
 * AdminBrainConfig.jsx · v3.5.0 · Panel de Cerebros (palancas globales)
 * ═══════════════════════════════════════════════════════════════════
 * UI para editar `system_config.brain.rules`:
 *   · A2: horizonte de días, buffer de traslado, máx días/técnico, batch
 *   · A1: reglas de pre-factibilidad (stock, geocoding)
 *   · Prioridades: VIP boost, peso antigüedad, boost por plan
 *   · v101.222.0 · Confirmación con el cliente (Fibi): directo / grupo de prueba / todos
 */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Brain, Save, RefreshCcw, Loader2, Sliders, ShieldCheck, Sparkles, Info,
  FolderTree, CheckCircle2, XCircle, MessageCircle,
} from "lucide-react";

const NUM_ROWS = [
  { section: "a2", key: "horizon_days", label: "Horizonte de días (A2)", alias: "MAX_DAYS_AHEAD", hint: "Cuántos días adelante puede agendar el motor (1–30).", suffix: "días" },
  { section: "a2", key: "buffer_min", label: "Buffer de traslado", alias: "A2_TRAVEL_BUFFER_MIN", hint: "Minutos obligatorios entre dos OTs del mismo técnico (0–120).", suffix: "min" },
  { section: "a2", key: "max_days_per_tech", label: "Máx. días por técnico", alias: "MAX_DAYS_PER_TECH", hint: "Cuántos días distintos puede tomar un técnico por corrida (1–10).", suffix: "días" },
  { section: "a2", key: "batch_limit", label: "Batch por corrida A2", alias: "BATCH_SIZE_A2", hint: "OTs procesadas por ciclo (10–1000).", suffix: "OTs" },
  { section: "priorities", key: "vip_boost", label: "Boost VIP", hint: "Puntos extra en el score A6 para clientes VIP (0–500).", suffix: "pts" },
  { section: "priorities", key: "seniority_w", label: "Peso antigüedad OT", hint: "Multiplicador de días desde el ingreso de la OT (0–5).", suffix: "x", step: 0.1 },
];

const BOOL_ROWS = [
  { section: "a1", key: "stock_required", label: "A1 · Bloquear sin stock", hint: "Si un material requerido no está disponible, la OT se marca a Revisión." },
  { section: "a1", key: "geocode_required", label: "A1 · Bloquear sin GPS", hint: "Si la dirección no se geocodifica, la OT no entra a la cola A6." },
];

export default function AdminBrainConfig() {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [ranges, setRanges] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [planKey, setPlanKey] = useState("");
  const [planBoost, setPlanBoost] = useState("");
  // v3.5.1 · sync product.category
  const [catStatus, setCatStatus] = useState(null);
  const [catSyncing, setCatSyncing] = useState(false);

  const loadCatStatus = async () => {
    try {
      const { data } = await api.get("/v1/catalog/categories/status");
      setCatStatus(data);
    } catch { /* silent */ }
  };
  const triggerCatSync = async () => {
    setCatSyncing(true);
    try {
      const { data } = await api.post("/v1/catalog/categories/sync");
      toast.success(`Sync OK · ${data.upserted} categorías (${data.duration_ms} ms)`);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Error de sync"));
    } finally {
      await loadCatStatus();
      setCatSyncing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/brain/config");
      setCfg(data.config);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      setRanges(data.ranges || {});
      setDirty(false);
    } catch (e) {
      toast.error("No se pudo cargar la configuración");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadCatStatus(); }, []);

  const setValue = (section, key, val) => {
    setCfg((prev) => ({ ...prev, [section]: { ...prev[section], [key]: val } }));
    setDirty(true);
  };

  const addPlanBoost = () => {
    if (!planKey.trim() || !planBoost.trim()) {
      toast.error("Ingresa nombre de plan y valor de boost");
      return;
    }
    const n = parseInt(planBoost, 10);
    if (isNaN(n)) return toast.error("Valor debe ser entero");
    setCfg((p) => ({
      ...p,
      priorities: {
        ...p.priorities,
        plan_boost: { ...(p.priorities.plan_boost || {}), [planKey.trim()]: n },
      },
    }));
    setPlanKey(""); setPlanBoost(""); setDirty(true);
  };
  const removePlanBoost = (k) => {
    setCfg((p) => {
      const pb = { ...(p.priorities.plan_boost || {}) };
      delete pb[k];
      return { ...p, priorities: { ...p.priorities, plan_boost: pb } };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/v1/brain/config", cfg);
      toast.success("Configuración guardada · A2 lo aplicará en el próximo ciclo");
      setCfg(data.config);
      setMeta({ updated_at: new Date().toISOString(), updated_by: data.updated_by });
      setDirty(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al guardar";
      toast.error(String(msg));
    } finally { setSaving(false); }
  };

  const resetToDefaults = () => {
    if (!window.confirm("¿Restaurar todas las palancas a los valores por defecto?")) return;
    setCfg({
      a2: { horizon_days: 3, buffer_min: 30, max_days_per_tech: 2, batch_limit: 500 },
      a1: { stock_required: true, geocode_required: true },
      priorities: { vip_boost: 100, seniority_w: 1.0, plan_boost: {} },
    });
    setDirty(true);
  };

  if (loading || !cfg) {
    return (
      <Layout title="Cerebros · Reglas Globales" subtitle="Cargando palancas del motor…">
        <div className="flex items-center justify-center h-64 text-gray-500">
          <Loader2 className="animate-spin mr-2" /> Cargando…
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Cerebros · Reglas Globales"
      subtitle="Ajusta las palancas del motor sin redeploy · v3.5.0"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="px-3 py-1.5 rounded border border-gray-300 bg-white text-xs flex items-center gap-1.5 hover:bg-gray-50"
            data-testid="brain-refresh"
          >
            <RefreshCcw size={12} /> Recargar
          </button>
          <button
            onClick={resetToDefaults}
            className="px-3 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs font-bold hover:bg-amber-100"
            data-testid="brain-reset"
          >
            Restaurar defaults
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className={`px-4 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 ${
              dirty && !saving ? "bg-[#0A3747] text-[#E0E621]" : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
            data-testid="brain-save"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* A2 · Agendamiento */}
        <section className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-4" data-testid="brain-section-a2">
          <header className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#EDE9FE] text-[#7C3AED] flex items-center justify-center">
              <Sliders size={16} />
            </div>
            <div>
              <div className="text-sm font-bold text-[#0A3747]">A2 · Agendamiento automático</div>
              <div className="text-[11px] text-gray-500">Motor de asignación de técnicos a OTs</div>
            </div>
          </header>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {NUM_ROWS.filter(r => r.section === "a2").map((row) => (
              <NumInput
                key={`${row.section}.${row.key}`}
                row={row}
                value={cfg[row.section][row.key]}
                onChange={(v) => setValue(row.section, row.key, v)}
                range={ranges[`${row.section}.${row.key}`]}
              />
            ))}
          </div>
        </section>

        {/* v101.222.0 · Confirmación con el cliente (Fibi) y grupo de prueba */}
        <ConfirmacionCliente a2={cfg.a2} onChange={(k, v) => setValue("a2", k, v)} />

        {/* A1 · Pre-factibilidad */}
        <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4" data-testid="brain-section-a1">
          <header className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#CFFAFE] text-[#0891B2] flex items-center justify-center">
              <ShieldCheck size={16} />
            </div>
            <div>
              <div className="text-sm font-bold text-[#0A3747]">A1 · Pre-factibilidad</div>
              <div className="text-[11px] text-gray-500">Reglas para bloquear OTs no viables</div>
            </div>
          </header>
          <div className="space-y-2">
            {BOOL_ROWS.map((row) => (
              <ToggleRow
                key={`${row.section}.${row.key}`}
                row={row}
                value={cfg[row.section][row.key]}
                onChange={(v) => setValue(row.section, row.key, v)}
              />
            ))}
          </div>
        </section>

        {/* Prioridades */}
        <section className="lg:col-span-3 bg-white border border-gray-200 rounded-xl shadow-sm p-4" data-testid="brain-section-priorities">
          <header className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] text-[#D97706] flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <div>
              <div className="text-sm font-bold text-[#0A3747]">Prioridades del score A6</div>
              <div className="text-[11px] text-gray-500">Ajusta el peso relativo de cada factor</div>
            </div>
          </header>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {NUM_ROWS.filter(r => r.section === "priorities").map((row) => (
              <NumInput
                key={`${row.section}.${row.key}`}
                row={row}
                value={cfg[row.section][row.key]}
                onChange={(v) => setValue(row.section, row.key, v)}
                range={ranges[`${row.section}.${row.key}`]}
              />
            ))}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Boost por tipo de plan</div>
            <div className="flex flex-wrap gap-2 mb-2" data-testid="brain-plan-boost-list">
              {Object.entries(cfg.priorities.plan_boost || {}).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1.5 bg-[#F3F4F6] border border-gray-200 rounded-full px-3 py-1 text-xs">
                  <span className="font-bold text-[#0A3747]">{k}</span>
                  <span className="text-gray-500">+{v}</span>
                  <button onClick={() => removePlanBoost(k)} className="text-red-500 hover:text-red-700 ml-1" data-testid={`brain-plan-boost-remove-${k}`}>×</button>
                </span>
              ))}
              {Object.keys(cfg.priorities.plan_boost || {}).length === 0 && (
                <span className="text-[11px] text-gray-400 italic">Sin boosts configurados</span>
              )}
            </div>
            <div className="flex gap-2 items-center">
              <input value={planKey} onChange={e => setPlanKey(e.target.value)} placeholder="Ej: residencial_600mbps"
                     className="flex-1 px-2 py-1.5 rounded border border-gray-300 text-xs" data-testid="brain-plan-boost-key" />
              <input value={planBoost} onChange={e => setPlanBoost(e.target.value)} placeholder="+puntos" type="number"
                     className="w-24 px-2 py-1.5 rounded border border-gray-300 text-xs" data-testid="brain-plan-boost-value" />
              <button onClick={addPlanBoost} className="px-3 py-1.5 rounded bg-[#0A3747] text-[#E0E621] text-xs font-bold" data-testid="brain-plan-boost-add">Añadir</button>
            </div>
          </div>
        </section>
        {/* v3.5.1 · Sync product.category */}
        <section className="lg:col-span-3 bg-white border border-gray-200 rounded-xl shadow-sm p-4" data-testid="brain-section-catalog">
          <header className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#DCFCE7] text-[#16A34A] flex items-center justify-center">
              <FolderTree size={16} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-[#0A3747]">Catálogo · product.category (Odoo)</div>
              <div className="text-[11px] text-gray-500">Réplica del árbol jerárquico usado en Recetas y Fiscalización</div>
            </div>
            <button
              onClick={triggerCatSync}
              disabled={catSyncing}
              className="px-3 py-1.5 rounded bg-[#0A3747] text-[#E0E621] text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
              data-testid="brain-cat-sync-btn"
            >
              {catSyncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
              Sincronizar ahora
            </button>
          </header>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <CatKpi label="Categorías totales" value={catStatus?.total ?? "—"} testid="brain-cat-total" />
            <CatKpi label="Raíces" value={catStatus?.roots ?? "—"} testid="brain-cat-roots" />
            <CatKpi label="Huérfanas" value={catStatus?.orphans ?? "—"}
                    highlight={catStatus?.orphans > 0} testid="brain-cat-orphans" />
            <CatKpi
              label="Última sync"
              value={catStatus?.worker?.last_sync_at
                ? new Date(catStatus.worker.last_sync_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil" })
                : "Nunca"}
              small
              testid="brain-cat-last-sync"
            />
          </div>
          {catStatus?.worker?.last_ok === false && (
            <div className="mt-2 text-[11px] text-red-600 flex items-center gap-1.5">
              <XCircle size={12} /> Última corrida falló: {catStatus.worker.last_error || "sin detalle"}
            </div>
          )}
          {catStatus?.worker?.last_ok === true && (
            <div className="mt-2 text-[11px] text-emerald-700 flex items-center gap-1.5">
              <CheckCircle2 size={12} /> Última corrida OK · {catStatus.worker.last_upserted} upserts en {catStatus.worker.last_duration_ms} ms · worker automático cada 1 h
            </div>
          )}
        </section>

        {/* v3.9.1 · La sección de Matriz de Fiscalización IA se movió al
            módulo de primer nivel `/fiscalizacion` (Centro Maestro). */}
      </div>

      {/* Meta */}
      <footer className="mt-4 flex items-center gap-2 text-[11px] text-gray-500">
        <Info size={11} />
        {meta.updated_by ? (
          <>Última edición por <span className="font-bold text-[#0A3747]">{meta.updated_by}</span> · {new Date(meta.updated_at).toLocaleString("es-EC")}</>
        ) : "Configuración usando defaults (nunca modificada)"}
      </footer>
    </Layout>
  );
}

function NumInput({ row, value, onChange, range }) {
  const [lo, hi] = range || [null, null];
  return (
    <label className="block" data-testid={`brain-field-${row.section}-${row.key}`}>
      <div className="text-[11px] font-bold text-[#0A3747] mb-1 flex items-center justify-between">
        <span>{row.label}</span>
        {(lo != null && hi != null) && <span className="text-[9px] font-normal text-gray-400">rango {lo}–{hi}</span>}
      </div>
      {row.alias && (
        <div className="text-[9px] font-mono text-purple-600 mb-1 tracking-wider">{row.alias}</div>
      )}
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          step={row.step || 1}
          value={value ?? ""}
          onChange={e => onChange(row.step ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
          className="flex-1 px-2 py-1.5 rounded border border-gray-300 text-xs font-bold text-[#0A3747]"
          data-testid={`brain-input-${row.section}-${row.key}`}
        />
        <span className="text-[10px] text-gray-500 min-w-[36px]">{row.suffix}</span>
      </div>
      {row.hint && <div className="text-[10px] text-gray-500 mt-1">{row.hint}</div>}
    </label>
  );
}

function ToggleRow({ row, value, onChange }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5" data-testid={`brain-toggle-${row.section}-${row.key}`}>
      <div className="min-w-0">
        <div className="text-xs font-bold text-[#0A3747]">{row.label}</div>
        <div className="text-[10px] text-gray-500">{row.hint}</div>
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`flex-shrink-0 relative w-9 h-5 rounded-full transition ${value ? "bg-[#0A3747]" : "bg-gray-300"}`}
        data-testid={`brain-toggle-btn-${row.section}-${row.key}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition ${value ? "left-4" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function CatKpi({ label, value, testid, highlight, small }) {
  return (
    <div className={`rounded-lg p-2 border ${highlight ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-white"}`}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
      <div className={`${small ? "text-[11px]" : "text-lg"} font-black text-[#0A3747] mt-0.5`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

// v101.222.0 · A2 hace la prefactibilidad, Fibi confirma la agenda con el cliente y luego A2 asigna.
const MODOS = [
  { v: "directo", t: "Directo (como antes)", d: "A2 asigna sin contactar al cliente." },
  { v: "prueba", t: "Con Fibi · solo grupo de prueba", d: "Solo los celulares del grupo siguen el flujo. El resto queda detenido: no se contacta ni se asigna." },
  { v: "todos", t: "Con Fibi · todos los clientes", d: "Fibi confirma la agenda con cada cliente y A2 asigna al confirmar." },
];

function ConfirmacionCliente({ a2, onChange }) {
  const modo = a2.confirmacion_modo || "directo";
  const [txt, setTxt] = useState((a2.grupo_prueba || []).join("\n"));
  useEffect(() => { setTxt((a2.grupo_prueba || []).join("\n")); }, [a2.grupo_prueba]);
  const cels = (t) => Array.from(new Set(String(t || "").split(/[\n,;]+/).map((x) => x.replace(/\D/g, "")).filter(Boolean)));
  const n = cels(txt).length;
  return (
    <section className="lg:col-span-3 bg-white border border-gray-200 rounded-xl shadow-sm p-4" data-testid="brain-section-fibi">
      <header className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#DCFCE7] text-[#16A34A] flex items-center justify-center">
          <MessageCircle size={16} />
        </div>
        <div>
          <div className="text-sm font-bold text-[#0A3747]">Confirmación con el cliente (Fibi)</div>
          <div className="text-[11px] text-gray-500">
            A2 hace la prefactibilidad → Fibi ofrece esos horarios por WhatsApp → con la confirmación del cliente, A2 asigna.
          </div>
        </div>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {MODOS.map((m) => (
          <button key={m.v} type="button" onClick={() => onChange("confirmacion_modo", m.v)}
                  data-testid={`brain-fibi-modo-${m.v}`}
                  className={`text-left rounded-lg border p-3 ${modo === m.v ? "border-[#0A3747] bg-[#F0F9FF] ring-1 ring-[#0A3747]" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
            <div className="text-xs font-bold text-[#0A3747]">{modo === m.v ? "● " : "○ "}{m.t}</div>
            <div className="text-[11px] text-gray-500 mt-1">{m.d}</div>
          </button>
        ))}
      </div>
      {modo === "prueba" && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">
            Grupo de prueba · {n} celular{n === 1 ? "" : "es"}
          </div>
          <textarea value={txt} rows={4} data-testid="brain-fibi-grupo"
                    onChange={(e) => { setTxt(e.target.value); onChange("grupo_prueba", cels(e.target.value)); }}
                    placeholder={"Un celular por línea (o separados por coma)\n0991234567"}
                    className="w-full px-2 py-1.5 rounded border border-gray-300 text-xs font-mono" />
          <div className="text-[11px] text-amber-700 mt-1">
            Con el autoagendamiento encendido, solo estos clientes reciben mensajes de Fibi (agenda, avisos de llegada y
            retraso) y se autoasignan. El resto de OTs queda en su carril para agenda manual, con el motivo en la OT.
          </div>
        </div>
      )}
      {modo !== "directo" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-xs text-gray-600">Saludos de Fibi por ciclo
            <input type="number" min={1} max={200} value={a2.fibi_max_contactos ?? 10} data-testid="brain-fibi-max"
                   onChange={(e) => onChange("fibi_max_contactos", parseInt(e.target.value || "0", 10))}
                   className="mt-1 w-full px-2 py-1.5 rounded border border-gray-300 text-xs" />
          </label>
          <label className="text-xs text-gray-600">Contactar desde (lun–sáb)
            <input type="time" value={a2.contacto_desde || "08:00"} data-testid="brain-fibi-desde"
                   onChange={(e) => onChange("contacto_desde", e.target.value)}
                   className="mt-1 w-full px-2 py-1.5 rounded border border-gray-300 text-xs" />
          </label>
          <label className="text-xs text-gray-600">Contactar hasta
            <input type="time" value={a2.contacto_hasta || "19:00"} data-testid="brain-fibi-hasta"
                   onChange={(e) => onChange("contacto_hasta", e.target.value)}
                   className="mt-1 w-full px-2 py-1.5 rounded border border-gray-300 text-xs" />
          </label>
        </div>
      )}
      {modo !== "directo" && (
        <div className="text-[11px] text-gray-500 mt-2">
          Requiere Fibi encendida (WhatsApp › Fibi) y el autoagendamiento encendido. Fibi escribe una sola vez por OT; si el
          cliente no responde, pasa a un agendador.
        </div>
      )}
    </section>
  );
}
